import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const MANUAL_STATUSES = ['new', 'contacted', 'followup', 'lost'];
const LOST_REASONS = ['price', 'competitor', 'no_response', 'not_qualified', 'other'];

const STATUS_CLASS = {
  new: 'lead-status-new',
  contacted: 'lead-status-contacted',
  followup: 'lead-status-followup',
  won: 'lead-status-won',
  lost: 'lead-status-lost',
};

function StatusPopover({
  anchorRef,
  wrapRef,
  lostStep,
  lostReason,
  lostNote,
  saving,
  displayStatus,
  t,
  onSelectStatus,
  onLostReasonChange,
  onLostNoteChange,
  onCancelLost,
  onConfirmLost,
  onStopPropagation,
}) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
    });
  }, [anchorRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition, lostStep]);

  return ReactDOM.createPortal(
    <div
      ref={wrapRef}
      className="lead-status-popover popover"
      style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 1060 }}
      onClick={onStopPropagation}
    >
      {!lostStep ? (
        <div className="lead-status-options">
          {MANUAL_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              className={`lead-status-option ${STATUS_CLASS[status]}`}
              disabled={saving || status === displayStatus}
              onClick={() => onSelectStatus(status)}
            >
              {t(`leads.status.${status}`)}
            </button>
          ))}
        </div>
      ) : (
        <div className="lead-status-lost-form">
          <label>{t('leads.lostReasonLabel')}</label>
          <select value={lostReason} onChange={(e) => onLostReasonChange(e.target.value)}>
            <option value="">{t('leads.lostReasonPlaceholder')}</option>
            {LOST_REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {t(`leads.lostReason.${reason}`)}
              </option>
            ))}
          </select>
          <textarea
            placeholder={t('leads.lostNotePlaceholder')}
            value={lostNote}
            onChange={(e) => onLostNoteChange(e.target.value)}
            rows={2}
          />
          <div className="lead-status-lost-actions">
            <button type="button" className="button button-small" onClick={onCancelLost}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="button button-small button-blue"
              disabled={!lostReason || saving}
              onClick={onConfirmLost}
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

export const LeadStatusBadge = ({
  entry,
  t,
  onStatusChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [lostStep, setLostStep] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [lostNote, setLostNote] = useState('');
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);
  const anchorRef = useRef(null);

  const displayStatus = entry.displayStatus || entry.status || 'new';
  const isWon = displayStatus === 'won';

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      const inWrap = wrapRef.current?.contains(e.target);
      const inAnchor = anchorRef.current?.contains(e.target);
      if (!inWrap && !inAnchor) {
        setOpen(false);
        setLostStep(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelectStatus = async (status) => {
    if (status === 'lost') {
      setLostStep(true);
      return;
    }
    setSaving(true);
    try {
      await onStatusChange(entry._id, { status });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmLost = async () => {
    if (!lostReason) return;
    setSaving(true);
    try {
      await onStatusChange(entry._id, { status: 'lost', lostReason, lostNote });
      setOpen(false);
      setLostStep(false);
      setLostReason('');
      setLostNote('');
    } finally {
      setSaving(false);
    }
  };

  if (isWon) {
    return (
      <span className={`lead-status-badge ${STATUS_CLASS.won}`}>
        {t('leads.status.won')}
      </span>
    );
  }

  return (
    <div className="lead-status-badge-wrap">
      <span
        ref={anchorRef}
        role="button"
        tabIndex={disabled || saving ? -1 : 0}
        className={`lead-status-badge ${STATUS_CLASS[displayStatus] || STATUS_CLASS.new}${disabled || saving ? ' lead-status-badge--disabled' : ''}`}
        onClick={(e) => {
          if (disabled || saving) return;
          e.stopPropagation();
          setOpen((v) => !v);
          setLostStep(false);
        }}
        onKeyDown={(e) => {
          if (disabled || saving) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
            setLostStep(false);
          }
        }}
      >
        {t(`leads.status.${displayStatus}`)}
      </span>
      {open && (
        <StatusPopover
          anchorRef={anchorRef}
          wrapRef={wrapRef}
          lostStep={lostStep}
          lostReason={lostReason}
          lostNote={lostNote}
          saving={saving}
          displayStatus={displayStatus}
          t={t}
          onSelectStatus={handleSelectStatus}
          onLostReasonChange={setLostReason}
          onLostNoteChange={setLostNote}
          onCancelLost={() => setLostStep(false)}
          onConfirmLost={handleConfirmLost}
          onStopPropagation={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
};
